import {trackGaPageView, trackMatomoPageView} from "./pageViews";

// constants; tweak them as needed
const URL_OAM = '/online-anmeldung/';
const URL_OAM_VIRTUAL = step => `${URL_OAM}schritt-${step}/`;
const URL_OAM_THANKS = `${URL_OAM}bestaetigung/`;
const URL_OAM_SEMINAR_VIRTUAL = (step, seminarSlug) => `${URL_OAM_VIRTUAL(step)}lehrgang/${seminarSlug}/`;
const URL_OAM_SEMINAR_THANKS = seminarSlug => `${URL_OAM_THANKS}lehrgang/${seminarSlug}/`;
const TITLE_OAM = page => `Schritt ${page} | Schule des Schreibens`;
const PATTERN_FORM_OAM = /^oamForm-\d+$/;
const PATTERN_OAM_STEP = /\/schritt-(\d+)/;
const PATTERN_SEMINAR_HASH = /^#\d+$/;
const FIELD_OAM_SEMINAR = 'seminar';

/**
 * Checks if the user is on the oam page and tries to parse and set the default value for the selected seminar.
 */
export function checkOamDefaultValue() {
    if (!isOnOamPage()) {
        if (window.location.pathname === URL_OAM_THANKS) oamThanksPV();
        return;
    }

    // is an oam page; extract seminarId from hash, if there is any

    const seminarId = findSeminarId();
    if (!seminarId) {
        // seminar is not set; initial page view has to be tracked nonetheless
        modifyInitialPageView(null);
    }

    // find form oam definition, if it exists
    const formDefKey = Object.keys(window.extendedforms)
        .find(id => typeof id === "string" && id.match(PATTERN_FORM_OAM));

    if (!formDefKey) return;

    const formDef = window.extendedforms[formDefKey];
    if (!formDef) return;

    // perform breadth-first search to find seminar select and replace default value
    const queue = [...formDef['configuration']['elements']];

    while (queue.length > 0) {
        const elem = queue.shift();
        if (!elem) continue;

        if (elem['identifier'] === FIELD_OAM_SEMINAR) {
            if (seminarId) {
                elem['defaultValue'] = seminarId;
            }

            // generates a statically accessible map of seminarId => slug, one can use this for tracking
            window.sds_seminars = elem['properties']['options'].reduce((dict, opt) => {
                dict[opt['value']] = opt['slug'];
                return dict;
            }, {});
            break;
        }

        if (elem['elements'] && Array.isArray(elem['elements'])) {
            queue.push(...elem['elements']);
        }
    }

    if (seminarId) {
        modifyInitialPageView(seminarId);
    }
}

/**
 * Manually fires the initial page view.
 * @param {string|null} seminarId The seminar id, if present.
 */
function modifyInitialPageView(seminarId) {
    if (!isOnOamPage()) return;

    // construct url and title
    const url = generateUrl(window.location.toString(), seminarId, 1);
    if (!url) return;

    const title = TITLE_OAM(1);

    // track initial virtual Matomo and GoogleAnalytics PageView
    trackMatomoPageView(url, title);
    trackGaPageView(url, title);
}

function oamThanksPV() {
    const urlPrefix = getUrlPrefix(window.location.toString());

    let url = null;

    if (typeof window.sds_seminars === 'object') {
        const seminarId = findSeminarId();
        if (seminarId) {
            const slug = window.sds_seminars[seminarId];
            if (slug) {
                url = urlPrefix + URL_OAM_SEMINAR_THANKS(slug);
            }
        }
    }

    if (!url) url = urlPrefix + URL_OAM_THANKS;

    const title = 'Bestätigung | Schule des Schreibens';

    trackMatomoPageView(url, title);
    trackGaPageView(url, title);
}

/**
 * Tries to identify the seminar by the current hash.
 * @return {string|null} The seminar id, or null if not present.
 */
function findSeminarId() {
    const hash = window.location.hash;
    return hash.match(PATTERN_SEMINAR_HASH) ? hash.substring(1) : null;
}

function isOnOamPage() {
    return window.location.pathname === URL_OAM;
}

/**
 * Intercepts MatomoPV callbacks.
 * @param {string} url The page view url.
 * @param {string} title The page view document title.
 * @param ctx The Vuex store context.
 * @return {boolean} True, whether the page view callback was handled.
 */
export function interceptOamMatomoPV(url, title, ctx) {
    if (!isOnOamPage()) return false;

    const override = handlePv(url, title, ctx);
    if (override) {
        const [modUrl, modTitle] = override;
        trackMatomoPageView(modUrl ? modUrl : url, modTitle ? modTitle : title);
        return true;
    }

    return false;
}

/**
 * Intercepts GoogleAnalyticsPV callbacks.
 * @param url The page view url.
 * @param title The page view document title.
 * @param ctx The Vuex store context.
 * @return {boolean} True, whether the page view callback was handled.
 */
export function interceptOamGaPV(url, title, ctx) {
    if (!isOnOamPage()) return false;

    const override = handlePv(url, title, ctx);
    if (override) {
        const [modUrl, modTitle] = override;
        trackGaPageView(modUrl ? modUrl : url, modTitle ? modTitle : title);
        return true;
    }

    return false;
}

/**
 * Tries to modify a page view for oam.
 * @param trackUrl The page view url.
 * @param trackTitle The page view document title.
 * @param ctx The Vuex store.
 * @return {[string, string]|null} The modified data.
 */
function handlePv(trackUrl, trackTitle, ctx) {
    let seminarId = null;

    const steps = ctx?.state?.steps;
    if (steps && Array.isArray(steps) && steps.length >= 1) {
        const seminar = steps[0]?.inputModel?.seminar?.value;
        if (typeof seminar === 'number') {
            seminarId = seminar;
        }
    }

    if (seminarId == null) {
        seminarId = findSeminarId();
    }

    // try to extract the page number from the trackUrl
    const match = trackUrl.match(PATTERN_OAM_STEP);
    if (!match) return null;

    const step = match[1];

    // construct url and title
    const url = generateUrl(trackUrl, seminarId, step);
    if (!url) return null;

    const title = TITLE_OAM(step);

    return [url, title];
}

/**
 * Generates an OAM url.
 * @param originalUrl The original url, which will provide everything before path.
 * @param seminarId The oam seminar id.
 * @param step The step.
 * @return {string|null} The generated url;
 */
function generateUrl(originalUrl, seminarId, step) {
    const urlPrefix = getUrlPrefix(originalUrl);
    let url = urlPrefix + URL_OAM_VIRTUAL(step);

    if (seminarId != null && typeof window.sds_seminars === 'object') {
        const slug = window.sds_seminars[seminarId];
        if (slug) {
            url = urlPrefix + URL_OAM_SEMINAR_VIRTUAL(step, slug);
        }
    }

    return url;
}

function getUrlPrefix(originalUrl) {
    let parsedUrl = null;
    try {
        parsedUrl = new URL(originalUrl);
        originalUrl = parsedUrl.toString();
    } catch (e) {
        return null;
    }

    const pathIdx = originalUrl.indexOf(parsedUrl.pathname);
    return originalUrl.substring(0, pathIdx);
}

/**
 * Handles a step change event.
 * @param {number} seminar The seminar id.
 */
export function handleOamStepChange(seminar) {
    if (!isOnOamPage()) return;

    const seminarId = seminar.toFixed(0);
    if (window.location.hash !== seminarId) {
        window.location.hash = seminarId;
    }
}
